<template>
  <div>
    <div
      v-if="loadError"
      class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-xl text-sm"
    >
      {{ loadError }}
    </div>

    <div
      v-else-if="!loading && nodes.length === 0"
      class="rounded-2xl border border-cyan-400/25 bg-slate-900/50 p-12 text-center text-slate-400 text-sm"
    >
      Nenhum agente neste ambiente. Use o <span class="text-cyan-300">Modo Card</span> e crie coordenadores e
      executores.
    </div>

    <div
      v-else
      class="rounded-2xl border border-cyan-400/25 bg-slate-950/50 backdrop-blur-sm overflow-hidden shadow-[0_0_40px_rgba(34,211,238,0.08)]"
    >
      <div v-if="loading" class="flex items-center justify-center min-h-[480px] text-slate-400 text-sm">
        Carregando rede…
      </div>
      <div v-else class="relative w-full overflow-x-auto overflow-y-hidden [scrollbar-width:thin]">
        <svg
          class="block shrink-0 max-h-[min(72vh,720px)] w-auto h-auto"
          :width="vbW"
          :height="vbH"
          :viewBox="`0 0 ${vbW} ${vbH}`"
          preserveAspectRatio="xMinYMid meet"
          role="img"
          aria-label="Mapa de ligações entre agentes"
        >
          <g
            class="network-edges"
            :class="{ 'network-edges--pulse-from-executor': edgePulseInverted }"
          >
            <path
              v-for="(e, idx) in edges"
              :key="`e-${e.from}-${e.to}-${idx}`"
              :d="edgePath(e)"
              fill="none"
              stroke-linecap="round"
              :class="edgeLineClass(e)"
              :style="edgeHighlightStyle(e)"
              vector-effect="non-scaling-stroke"
            />
          </g>

          <g class="network-nodes">
            <g
              v-for="n in nodes"
              :key="n.id"
              :transform="`translate(${nodePos(n.id).x}, ${nodePos(n.id).y})`"
              class="network-node cursor-pointer"
              role="button"
              :tabindex="n.agent_type === 'coordinator' || n.agent_type === 'executor' ? 0 : -1"
              @click="onNodeClick(n)"
              @keydown.enter.prevent="onNodeClick(n)"
            >
              <circle
                :r="nodeBackdropRadius(n)"
                :class="[
                  'node-backdrop',
                  nodeBackdropKind(n),
                  { 'node-backdrop--executor': n.agent_type === 'executor' }
                ]"
              />
              <foreignObject
                x="-34"
                y="-34"
                width="68"
                height="68"
                overflow="visible"
                style="overflow: visible"
                class="pointer-events-none network-node-avatar-fo"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  class="network-node-avatar-fo-root w-full h-full flex items-center justify-center overflow-visible"
                >
                  <div class="network-node-avatar-scale-wrap overflow-visible">
                    <AgentAvatar
                      :variant="n.avatar_variant"
                      :color="n.avatar_color"
                      :name="n.name"
                      size="md"
                      :show-coordinator-star="false"
                      ring-cyan
                      ring-cyan-tight-glow
                    />
                  </div>
                </div>
              </foreignObject>
              <text
                :y="nodeLabelBaselineY(n)"
                text-anchor="middle"
                class="fill-slate-200 text-[11px] font-medium pointer-events-none"
                style="font-family: system-ui, sans-serif"
              >
                {{ truncateName(n.name) }}
              </text>
              <foreignObject
                v-if="isSelectedEditableNode(n)"
                :x="nodeEditButtonForeignObjectX(n)"
                :y="nodeEditButtonForeignObjectY(n)"
                :width="nodeEditButtonForeignObjectW(n)"
                height="28"
                class="network-node-edit-fo overflow-visible"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  :class="nodeEditButtonWrapClass(n)"
                >
                  <button
                    type="button"
                    class="network-node-edit-btn"
                    :title="'Editar ' + (n.name || 'agente')"
                    @click.stop="onEditNodeClick(n)"
                  >
                    Editar
                  </button>
                </div>
              </foreignObject>
            </g>
          </g>
        </svg>
      </div>

      <div
        v-if="!loading && nodes.length"
        class="flex flex-wrap gap-6 px-4 py-3 border-t border-cyan-400/15 bg-slate-900/40 text-xs text-slate-400"
      >
        <span class="inline-flex items-center gap-2">
          <span class="w-9 h-1 rounded-sm bg-sky-400/75 shrink-0" />
          Ligação coordenador → executor
        </span>
        <span class="inline-flex items-center gap-2 text-cyan-300/90">
          <span class="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee] animate-pulse shrink-0" />
          Pulso = ligação ao nó selecionado
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi'
import AgentAvatar from './AgentAvatar.vue'
import {
  DEFAULT_AGENT_AVATAR_VARIANT,
  DEFAULT_AGENT_AVATAR_COLOR
} from '../constants/agentAvatarPresets.js'

const VB_W = 920
const VB_H = 600
/** Espaço mínimo entre centros verticais na mesma coluna (coordenadores e executores). */
const MIN_NODE_COLUMN_VERTICAL_GAP = 78
/** Distância horizontal entre colunas (coordenadores à esquerda; executores à direita). */
const COORDINATOR_COLUMN_STEP = 160
const EXECUTOR_COLUMN_STEP = 160
/** Distância do centro da última coluna de coordenadores ao centro da primeira coluna de executores. */
const COORD_EXECUTOR_GAP = 260

export default {
  name: 'AgentNetwork',

  emits: ['loading-state', 'edit-selected'],

  components: { AgentAvatar },

  setup() {
    const { get } = useApi()
    return { get }
  },

  data() {
    return {
      vbW: VB_W,
      vbH: VB_H,
      nodes: [],
      edges: [],
      positions: {},
      loading: true,
      loadError: null,
      selectedNodeId: null,
      edgeSpreadByKey: {}
    }
  },

  computed: {
    edgePulseInverted() {
      if (this.selectedNodeId == null) return false
      const n = this.nodes.find((x) => String(x.id) === String(this.selectedNodeId))
      return !!(n && n.agent_type === 'executor')
    }
  },

  mounted() {
    this.loadNetwork()
  },

  methods: {
    truncateName(name) {
      const s = (name || '').trim()
      if (s.length <= 18) return s
      return `${s.slice(0, 16)}…`
    },

    async loadNetwork() {
      this.loading = true
      this.$emit('loading-state', true)
      this.loadError = null
      try {
        const { data, error } = await this.get('/api/v1/agents/network')
        if (error || !data) {
          this.loadError = 'Não foi possível carregar a rede de agentes.'
          this.nodes = []
          this.edges = []
          this.positions = {}
          this.vbW = VB_W
          return
        }
        const rawNodes = data.nodes || []
        this.nodes = rawNodes.map((n) => ({
          id: n.id,
          name: n.name,
          agent_type: n.agent_type,
          avatar_variant: n.avatar_variant || DEFAULT_AGENT_AVATAR_VARIANT,
          avatar_color: n.avatar_color || DEFAULT_AGENT_AVATAR_COLOR
        }))
        this.edges = (data.edges || []).map((e) => ({
          from: e.from,
          to: e.to
        }))
        this.layoutNodes()
        this.assignEdgeSpread()
        this.selectedNodeId = null
      } finally {
        this.loading = false
        this.$emit('loading-state', false)
      }
    },

    layoutNodes() {
      const coords = this.nodes.filter((n) => n.agent_type === 'coordinator')
      const execs = this.nodes.filter((n) => n.agent_type === 'executor')
      const leftX = 130
      const top = 70
      const bottom = this.vbH - 90
      const span = Math.max(1, bottom - top)
      const pos = {}

      /**
       * Colunas com padrão tijolo: pares na grade, ímpares com meio passo (cabem em [top,bottom]).
       */
      const placeStaggeredColumn = (list, x, colIndex) => {
        const n = list.length
        if (n === 0) return
        if (n === 1) {
          pos[list[0].id] = { x, y: (top + bottom) / 2 }
          return
        }
        if (colIndex % 2 === 0) {
          const gap = span / (n - 1)
          list.forEach((node, i) => {
            pos[node.id] = { x, y: top + i * gap }
          })
        } else {
          const g = span / (n - 0.5)
          list.forEach((node, i) => {
            pos[node.id] = { x, y: top + i * g + g / 2 }
          })
        }
      }

      /** Um a menos que o máximo “apertado”, para sobrar espaço vertical ao botão Editar sob o nó selecionado. */
      const maxPerColumn = Math.max(1, Math.floor(span / MIN_NODE_COLUMN_VERTICAL_GAP))

      const coordColCount =
        coords.length === 0 ? 0 : Math.ceil(coords.length / maxPerColumn)
      const executorColCount =
        execs.length === 0 ? 0 : Math.ceil(execs.length / maxPerColumn)

      /** Última coluna de coordenadores (centro X); se não houver, usa referência da 1ª coluna “virtual”. */
      const lastCoordColCenter =
        coordColCount === 0 ? leftX : leftX + (coordColCount - 1) * COORDINATOR_COLUMN_STEP
      /** Primeira coluna de executores: após o bloco de coordenadores + separação. */
      const executorStartX =
        coords.length === 0
          ? leftX + COORD_EXECUTOR_GAP
          : lastCoordColCenter + COORD_EXECUTOR_GAP

      const rightMargin = 130
      const coordBlockRightEdge = coordColCount === 0 ? leftX : lastCoordColCenter + COORDINATOR_COLUMN_STEP / 2
      const executorBlockRightEdge =
        executorColCount === 0
          ? executorStartX
          : executorStartX + (executorColCount - 1) * EXECUTOR_COLUMN_STEP
      const contentW = Math.max(coordBlockRightEdge, executorBlockRightEdge) + rightMargin

      this.vbW = Math.max(VB_W, contentW)

      for (let c = 0; c < coordColCount; c++) {
        const slice = coords.slice(c * maxPerColumn, (c + 1) * maxPerColumn)
        const x = leftX + c * COORDINATOR_COLUMN_STEP
        placeStaggeredColumn(slice, x, c)
      }

      for (let c = 0; c < executorColCount; c++) {
        const slice = execs.slice(c * maxPerColumn, (c + 1) * maxPerColumn)
        const x = executorStartX + c * EXECUTOR_COLUMN_STEP
        placeStaggeredColumn(slice, x, c)
      }

      this.nodes.forEach((node) => {
        if (!pos[node.id]) {
          const x = node.agent_type === 'coordinator' ? leftX : executorStartX
          pos[node.id] = { x, y: (top + bottom) / 2 }
        }
      })

      this.positions = pos
    },

    edgeKey(e) {
      return `${String(e.from)}-${String(e.to)}`
    },

    /**
     * Várias ligações ao mesmo executor recebem curvas deslocadas para não sobrepor o path SVG.
     */
    assignEdgeSpread() {
      const spread = {}
      const crossCol = (e) => {
        const p1 = this.nodePos(e.from)
        const p2 = this.nodePos(e.to)
        return Math.abs(p1.x - p2.x) >= 80
      }

      const byTarget = {}
      this.edges.forEach((e) => {
        if (!crossCol(e)) return
        const t = String(e.to)
        if (!byTarget[t]) byTarget[t] = []
        byTarget[t].push(e)
      })
      Object.keys(byTarget).forEach((t) => {
        const list = byTarget[t].slice().sort((a, b) => String(a.from).localeCompare(String(b.from)))
        const n = list.length
        const step = 26
        list.forEach((e, i) => {
          spread[this.edgeKey(e)] = (i - (n - 1) / 2) * step
        })
      })

      const bySource = {}
      this.edges.forEach((e) => {
        if (crossCol(e)) return
        const s = String(e.from)
        if (!bySource[s]) bySource[s] = []
        bySource[s].push(e)
      })
      Object.keys(bySource).forEach((s) => {
        const list = bySource[s].slice().sort((a, b) => String(a.to).localeCompare(String(b.to)))
        const n = list.length
        const step = 22
        list.forEach((e, i) => {
          spread[this.edgeKey(e)] = (i - (n - 1) / 2) * step
        })
      })

      this.edgeSpreadByKey = spread
    },

    edgeSpreadPx(e) {
      return this.edgeSpreadByKey[this.edgeKey(e)] || 0
    },

    nodePos(id) {
      return this.positions[id] || { x: this.vbW / 2, y: this.vbH / 2 }
    },

    edgePath(e) {
      const p1 = this.nodePos(e.from)
      const p2 = this.nodePos(e.to)
      const spread = this.edgeSpreadPx(e)
      const sameSide = Math.abs(p1.x - p2.x) < 80
      if (sameSide) {
        const bulge = 140 + spread
        const midX = Math.max(p1.x, p2.x) + bulge
        return `M ${p1.x} ${p1.y} C ${midX} ${p1.y} ${midX} ${p2.y} ${p2.x} ${p2.y}`
      }
      const mx = (p1.x + p2.x) / 2 + spread
      return `M ${p1.x} ${p1.y} C ${mx} ${p1.y} ${mx} ${p2.y} ${p2.x} ${p2.y}`
    },

    edgeTouchesSelection(e) {
      if (this.selectedNodeId == null) return false
      const sel = String(this.selectedNodeId)
      const node = this.nodes.find((n) => String(n.id) === sel)
      if (!node) return false
      if (node.agent_type === 'coordinator') {
        return String(e.from) === sel
      }
      if (node.agent_type === 'executor') {
        return String(e.to) === sel
      }
      return false
    },

    edgeHighlightStyle(e) {
      if (this.selectedNodeId == null || !this.edgeTouchesSelection(e)) return {}
      const a = Number(e.from) || 0
      const b = Number(e.to) || 0
      const ms = ((a * 17 + b * 31) % 25) * 80
      return { animationDelay: `${ms}ms` }
    },

    edgeLineClass(e) {
      const base = 'network-edge'
      if (this.selectedNodeId == null) {
        return `${base} network-edge--normal`
      }
      if (this.edgeTouchesSelection(e)) {
        return `${base} network-edge--highlighted`
      }
      return `${base} network-edge--faded`
    },

    nodeBackdropRadius(n) {
      return n.agent_type === 'executor' ? 21 : 34
    },

    nodeLabelBaselineY(n) {
      return n.agent_type === 'executor' ? 40 : 52
    },

    /** Coordenador: Editar à esquerda do anel; executor: à direita. Centro vertical alinhado ao círculo. */
    nodeEditButtonForeignObjectX(n) {
      const gap = 8
      const btnAreaW = 52
      const r = this.nodeBackdropRadius(n)
      if (n.agent_type === 'coordinator') {
        return -(r + gap + btnAreaW)
      }
      return r + gap
    },
    nodeEditButtonForeignObjectY(n) {
      return -14
    },
    nodeEditButtonForeignObjectW() {
      return 52
    },
    nodeEditButtonWrapClass(n) {
      return n.agent_type === 'coordinator'
        ? 'flex h-full items-center justify-end pr-0.5'
        : 'flex h-full items-center justify-start pl-0.5'
    },

    nodeBackdropKind(n) {
      if (this.selectedNodeId == null) return ''
      const sel = String(this.selectedNodeId) === String(n.id)
      if (sel) return 'node-backdrop--selected'
      if (this.edgeTouchesNode(n.id)) return 'node-backdrop--linked'
      return ''
    },

    edgeTouchesNode(nodeId) {
      const nid = String(nodeId)
      return this.edges.some(
        (e) =>
          this.edgeTouchesSelection(e) &&
          (String(e.from) === nid || String(e.to) === nid)
      )
    },

    onNodeClick(n) {
      if (n.agent_type !== 'coordinator' && n.agent_type !== 'executor') return
      const id = n.id
      this.selectedNodeId = String(this.selectedNodeId) === String(id) ? null : id
    },

    isSelectedEditableNode(n) {
      if (this.selectedNodeId == null) return false
      if (String(this.selectedNodeId) !== String(n.id)) return false
      return n.agent_type === 'coordinator' || n.agent_type === 'executor'
    },

    onEditNodeClick(n) {
      if (!this.isSelectedEditableNode(n)) return
      this.$emit('edit-selected', n.id)
    }
  }
}
</script>

<style scoped>
.network-edge--normal {
  stroke: #7dd3fc;
  stroke-width: 1.35;
  opacity: 0.28;
}

.network-edge--faded {
  stroke: #64748b;
  stroke-width: 1.15;
  opacity: 0.14;
}

.network-edge--highlighted {
  stroke: #22d3ee;
  stroke-width: 2.1;
  opacity: 1;
  stroke-dasharray: 1.25 10.75;
  stroke-dashoffset: 0;
  filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.95))
    drop-shadow(0 0 14px rgba(34, 211, 238, 0.55))
    drop-shadow(0 0 22px rgba(6, 182, 212, 0.35));
  animation: network-neural-flow 0.275s linear infinite;
}

.network-edges--pulse-from-executor .network-edge--highlighted {
  animation-direction: reverse;
}

@media (prefers-reduced-motion: reduce) {
  .network-edge--highlighted {
    animation: none;
    stroke-dasharray: none;
  }
}

@keyframes network-neural-flow {
  to {
    stroke-dashoffset: -12;
  }
}

.network-node:focus,
.network-node:focus-visible {
  outline: none;
}

.node-backdrop {
  fill: rgba(15, 23, 42, 0.5);
  stroke: rgba(34, 211, 238, 0.3);
  stroke-width: 1;
}

.node-backdrop--executor {
  stroke: none;
}

.node-backdrop--selected {
  fill: rgba(6, 182, 212, 0.14);
  stroke: #22d3ee;
  stroke-width: 2.5;
  filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.48));
}

/* Outro extremo da seleção: coordenador ↔ executor, alinhado ao pulso das arestas */
.node-backdrop--linked {
  fill: rgba(6, 182, 212, 0.16);
  stroke: #22d3ee;
  stroke-width: 2.25;
  filter: drop-shadow(0 0 5px rgba(34, 211, 238, 0.5)) drop-shadow(0 0 14px rgba(34, 211, 238, 0.28));
}

.network-node-edit-fo {
  pointer-events: all;
}

.network-node-edit-btn {
  font-family: system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid rgba(34, 211, 238, 0.5);
  background: rgba(15, 23, 42, 0.92);
  color: #a5f3fc;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.network-node-edit-btn:hover {
  background: rgba(34, 211, 238, 0.12);
  border-color: rgba(34, 211, 238, 0.75);
  color: #ecfeff;
}

.network-node-edit-btn:focus {
  outline: none;
}

.network-node-edit-btn:focus-visible {
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.95), 0 0 0 4px rgba(34, 211, 238, 0.55);
}

.network-node-avatar-scale-wrap > * {
  transform: scale(calc(48 / 56));
  transform-origin: center center;
}

.network-node-avatar-fo,
.network-node-avatar-fo-root {
  overflow: visible !important;
}
</style>
